// Grupos del flujo de «Mis tareas» y nombres simples de `Status` (docs/SPEC.md «Cambios v2»,
// docs/PLAN-V2.md §2.1). Flujo: En preparación / En pausa → Por hacer → Haciendo → Entregado.

import { sortCards, type SortableCard } from './order.ts';
import { findStatusOption, statusKey } from './status.ts';

export type FlowGroup = 'preparacion' | 'pausa' | 'por_hacer' | 'haciendo' | 'entregado';

export interface FlowGroupInfo {
  readonly key: FlowGroup;
  readonly label: string;
  /** Los grupos previos a Por hacer van plegados: la lista se ve desde Por hacer. */
  readonly folded: boolean;
}

export const FLOW_GROUPS: readonly FlowGroupInfo[] = [
  { key: 'preparacion', label: 'En preparación', folded: true },
  { key: 'pausa', label: 'En pausa', folded: true },
  { key: 'por_hacer', label: 'Por hacer', folded: false },
  { key: 'haciendo', label: 'Haciendo', folded: false },
  { key: 'entregado', label: 'Entregado', folded: false },
];

/** Grupo de cada opción de `Status`; null: no aparece en Mis tareas. */
const GROUP_BY_STATUS: Record<string, FlowGroup | null> = {
  'Not started': 'preparacion',
  '💰 REVISIÓN PRECIOS': 'preparacion',
  '⏳ STAND BY': 'pausa',
  ASAP: 'por_hacer',
  '🏁 POR HACER': 'por_hacer',
  '👨‍💻 EN PROGRESO': 'haciendo',
  '🔙 VUELTA CORRECCIÓN': 'haciendo',
  '☑️ ENTREGADO POR PROGRAMACIÓN': 'entregado',
  '🧠 AYUDA MEMORIA TABLERO': null,
  '✅ TERMINADO Y CERRADO': null,
};

/**
 * Grupo del flujo. Un `Status` que Tablero no conoce (una opción nueva en Notion) va a En
 * preparación: se ve, plegado, en vez de desaparecer.
 */
export function flowGroup(status: string | null | undefined): FlowGroup | null {
  const option = findStatusOption(status);
  if (!option) return 'preparacion';
  return GROUP_BY_STATUS[option.name] ?? null;
}

/**
 * `Status` que recibe una tarjeta al soltarla en la columna de un grupo (Kanban de Mis tareas).
 * Es la opción común del grupo: Urgente (ASAP) y Corrección se eligen en la tarjeta.
 */
export const GROUP_DROP_STATUS: Readonly<Record<FlowGroup, string>> = {
  preparacion: 'Not started',
  pausa: '⏳ STAND BY',
  por_hacer: '🏁 POR HACER',
  haciendo: '👨‍💻 EN PROGRESO',
  entregado: '☑️ ENTREGADO POR PROGRAMACIÓN',
};

/** Nombres simples para cambiar el `Status` desde la tarjeta («Más estados» muestra todos). */
export const SIMPLE_STATUSES: readonly { readonly label: string; readonly status: string }[] = [
  { label: 'Por hacer', status: '🏁 POR HACER' },
  { label: 'Urgente', status: 'ASAP' },
  { label: 'Haciendo', status: '👨‍💻 EN PROGRESO' },
  { label: 'Corrección', status: '🔙 VUELTA CORRECCIÓN' },
  { label: 'Entregado', status: '☑️ ENTREGADO POR PROGRAMACIÓN' },
];

/** Nombre simple del `Status`, o null si no tiene (se muestra el de Notion). */
export function simpleStatus(status: string | null | undefined): string | null {
  const option = findStatusOption(status);
  if (!option) return null;
  const key = statusKey(option.name);
  return SIMPLE_STATUSES.find((s) => statusKey(s.status) === key)?.label ?? null;
}

/** Marca de la fila en Mis tareas: «Urgente» (ASAP) y «Corrección» (VUELTA CORRECCIÓN). */
export function statusMark(status: string | null | undefined): 'Urgente' | 'Corrección' | null {
  const label = simpleStatus(status);
  return label === 'Urgente' || label === 'Corrección' ? label : null;
}

export interface FlowSection<T> extends FlowGroupInfo {
  cards: T[];
}

/**
 * Grupos no vacíos en el orden del flujo. Dentro de cada grupo rige el orden de la SPEC
 * (opción de Status, prioridad, `Fechas.start`, creación): ASAP queda antes que POR HACER.
 */
export function groupByFlow<T extends SortableCard>(cards: readonly T[]): FlowSection<T>[] {
  const byGroup = new Map<FlowGroup, T[]>();
  for (const card of sortCards(cards)) {
    const group = flowGroup(card.status);
    if (!group) continue;
    const list = byGroup.get(group) ?? [];
    list.push(card);
    byGroup.set(group, list);
  }
  return FLOW_GROUPS.filter((g) => byGroup.has(g.key)).map((g) => ({
    ...g,
    cards: byGroup.get(g.key) ?? [],
  }));
}
